import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isRTL } from '@/core/i18n'
import { Plus, Search, Edit, Printer, ChevronLeft, ChevronRight, Copy, FileDown, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostError, showSuccess } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ProcessingModal, ProcessingStepStatus } from '@/components/shared/ProcessingModal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { quotationsService } from '@/services/quotationsService'
import { financeService } from '@/services/financeService'
import { QuotationListItem, FinanceCustomer } from '@/api/types'

/**
 * Quotations List Page
 * Displays a table of quotations with pagination and customer filter
 */
export const QuotationsList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const rtl = isRTL(i18n.language)
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [quotations, setQuotations] = useState<QuotationListItem[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [postingQuotationId, setPostingQuotationId] = useState<number | null>(null)
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [processingSteps, setProcessingSteps] = useState<
    Array<{ label: string; status: ProcessingStepStatus }>
  >([
    { label: t('quotations.quotationIsPosting'), status: 'pending' },
  ])

  const branchId = user?.selectedBranch?.branchId

  // Fetch customers for filter
  useEffect(() => {
    if (!branchId) return

    const fetchCustomers = async () => {
      try {
        const data = await financeService.getAllCustomers(branchId)
        setCustomers(data)
      } catch (error: any) {
        // Error handled silently
      }
    }

    fetchCustomers()
  }, [branchId])

  // Fetch quotations
  useEffect(() => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }

    const fetchQuotations = async () => {
      try {
        setLoading(true)
        
        let data: QuotationListItem[] = []
        let total = 0

        if (selectedCustomerId) {
          // Filter by customer
          data = await quotationsService.getQuotationsByCustomer(branchId, selectedCustomerId)
          total = data.length
          setTotalPages(1)
        } else {
          // Get paginated list
          const response = await quotationsService.getAllQuotations(branchId, pageNumber, pageSize)
          data = response.data || []
          total = response.totalRecords || 0
          const pages = total ? Math.ceil(total / response.pageSize) : 0
          setTotalPages(pages)
        }

        setQuotations(data)
        setTotalCount(total)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setQuotations([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('quotations.failedToLoadQuotations'))
        }
      } finally {
        setLoading(false)
      }
    }

    fetchQuotations()
  }, [branchId, pageNumber, pageSize, selectedCustomerId])

  // Filter quotations by search query
  const filteredQuotations = quotations.filter((q) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      (q.quotationNo || q.quotationNumber || '').toLowerCase().includes(query) ||
      (q.poNumber || q.refNo || '').toLowerCase().includes(query) ||
      (q.customerName || '').toLowerCase().includes(query) ||
      (q.projectName || (q.projectId ? `Project ${q.projectId}` : '')).toLowerCase().includes(query)
    )
  })

  const handleAddQuotation = () => {
    navigate('/quotations/new')
  }

  const handleEditQuotation = (quotation: QuotationListItem) => {
    // Check if quotation is already posted
    const isPosted = quotation.isPosted === true || quotation.isPosted === 1
    if (isPosted) {
      toast.error(t('quotations.alreadyConverted'))
      return
    }

    const quotationId = quotation.id || quotation.quotationId || 0
    // Pass customerId via state if available
    const customerId = quotation.customerId
    navigate(`/quotations/${quotationId}/edit`, { 
      state: customerId ? { customerId } : undefined 
    })
  }

  const handleConvertToInvoice = async (quotation: QuotationListItem) => {
    const quotationId = quotation.id || quotation.quotationId || 0
    if (!quotationId) {
      toast.error(t('quotations.invalidQuotationId'))
      return
    }

    // Check if already posted
    const isPosted = quotation.isPosted === true || quotation.isPosted === 1
    if (isPosted) {
      toast.error(t('quotations.alreadyConverted'))
      return
    }

    // Reset steps and show modal
    setProcessingSteps([
      { label: t('quotations.quotationIsPosting'), status: 'pending' },
    ])
    setShowProcessingModal(true)
    setPostingQuotationId(quotationId)

    try {
      // Make API call
      const response = await quotationsService.convertToInvoice(quotationId)
      
      // Update step to completed
      await new Promise((resolve) => setTimeout(resolve, 500))
      setProcessingSteps((prev) =>
        prev.map((step) => ({ ...step, label: 'Posted', status: 'completed' as ProcessingStepStatus }))
      )

      // Wait a bit before closing modal
      await new Promise((resolve) => setTimeout(resolve, 500))

      if (response.result.isError) {
        toast.error(response.result.msg || t('quotations.failedToConvert'))
      } else {
        await showSuccess(
          t('quotations.postedTitle', { defaultValue: 'Posted' }),
          response.result.msg || t('quotations.convertedSuccess'),
          2500
        )
      }

      // Refresh the list
      const fetchQuotations = async () => {
        try {
          let data: QuotationListItem[] = []
          let total = 0

          if (selectedCustomerId) {
            data = await quotationsService.getQuotationsByCustomer(branchId!, selectedCustomerId)
            total = data.length
            setTotalPages(1)
          } else {
            const response = await quotationsService.getAllQuotations(branchId!, pageNumber, pageSize)
            data = response.data || []
            total = response.totalRecords || 0
            const pages = total ? Math.ceil(total / response.pageSize) : 0
            setTotalPages(pages)
          }

          setQuotations(data)
          setTotalCount(total)
        } catch (error: any) {
          // Error handled silently
        }
      }
      fetchQuotations()

      setShowProcessingModal(false)
    } catch (error: any) {
      console.error('Error converting quotation to invoice:', error)
      
      // Mark current step as error
      setProcessingSteps((prev) => {
        return prev.map((step) => ({ ...step, status: 'error' as ProcessingStepStatus }))
      })

      // Wait a bit before closing modal and showing error
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowProcessingModal(false)
      await showPostError(error)
    } finally {
      setPostingQuotationId(null)
    }
  }

  const handlePrintQuotation = (quotationId: number) => {
    navigate(`/quotations/${quotationId}/print`)
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1)
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1)
    }
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1)
    }
  }

  const handleCustomerFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedCustomerId(undefined)
    } else {
      setSelectedCustomerId(Number(value))
    }
    setPageNumber(1) // Reset to first page when filter changes
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return amount.toFixed(2)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('navigation.quotations')}
          description={t('quotations.manageQuotations')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('quotations.branchNotSelected')}
              description={t('quotations.selectBranchToViewQuotations')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('navigation.quotations')}
        description={t('quotations.manageQuotations')}
        action={{
          label: t('quotations.addNew'),
          onClick: handleAddQuotation,
          icon: Plus,
        }}
      />

      <ProcessingModal open={showProcessingModal} steps={processingSteps} />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('quotations.customer')}</label>
              <Select value={selectedCustomerId ? String(selectedCustomerId) : 'all'} onValueChange={handleCustomerFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('quotations.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('quotations.all')}</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('quotations.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('quotations.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" className="flex-1 sm:flex-initial">
                <Copy className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('quotations.copy')}</span>
              </Button>
              <Button variant="outline" size="sm" className="flex-1 sm:flex-initial">
                <FileDown className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('quotations.csv')}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('quotations.loadingQuotations')}</p>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('quotations.noQuotationsFound')}
                description={
                  searchQuery || selectedCustomerId
                    ? t('quotations.tryAdjustingSearch')
                    : t('quotations.getStarted')
                }
                action={
                  !searchQuery && !selectedCustomerId
                    ? {
                        label: t('quotations.addNewQuotation'),
                        onClick: handleAddQuotation,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.id')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.quotationNumber')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.customer')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary hidden lg:table-cell ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.project')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.date')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.amount')}
                      </TableHead>
                      <TableHead className={`h-12 px-2 sm:px-3 bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary ${rtl ? 'text-right pr-6' : 'text-left pl-6'}`}>
                        {t('quotations.status')}
                      </TableHead>
                      <TableHead className="h-12 px-2 sm:px-3 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('quotations.post')}
                      </TableHead>
                      <TableHead className="h-12 px-2 sm:px-3 text-right bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('quotations.actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotations.map((quotation) => (
                      <TableRow 
                        key={quotation.id || quotation.quotationId} 
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {quotation.id || quotation.quotationId || 'N/A'}
                        </TableCell>
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm font-medium ${rtl ? 'text-right' : 'text-left'}`}>
                          {quotation.quotationNo || quotation.quotationNumber || 'N/A'}
                        </TableCell>
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {quotation.customerName || 'N/A'}
                        </TableCell>
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm hidden lg:table-cell ${rtl ? 'text-right' : 'text-left'}`}>
                          {quotation.projectName || (quotation.projectId ? `Project ${quotation.projectId}` : '-')}
                        </TableCell>
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {formatDate(quotation.quotationDate)}
                        </TableCell>
                        <TableCell className={`px-2 sm:px-3 py-2 text-sm ${rtl ? 'text-right' : 'text-left'}`}>
                          {formatCurrency(quotation.total || quotation.amount)}
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2">
                          {(() => {
                            const isPosted = quotation.isPosted === true || quotation.isPosted === 1
                            const statusText = isPosted ? 'INV-Posted' : 'INV-Unposted'
                            const statusVariant = isPosted ? 'default' : 'secondary'
                            
                            return (
                              <Badge 
                                variant={statusVariant as any} 
                                className="text-xs"
                              >
                                {statusText}
                              </Badge>
                            )
                          })()}
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2 text-center">
                          {(() => {
                            const isPosted = quotation.isPosted === true || quotation.isPosted === 1
                            const quotationId = quotation.id || quotation.quotationId || 0
                            const isPosting = postingQuotationId === quotationId
                            
                            return (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleConvertToInvoice(quotation)}
                                disabled={isPosted || isPosting}
                                className={isPosted ? 'text-green-600' : ''}
                                title={isPosting ? t('quotations.posting') : !isPosted ? t('quotations.convertToInvoice') : t('quotations.alreadyConverted')}
                              >
                                {isPosting ? (
                                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></span>
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                            )
                          })()}
                        </TableCell>
                        <TableCell className="px-2 sm:px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrintQuotation(quotation.id || quotation.quotationId || 0)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditQuotation(quotation)}
                              disabled={quotation.isPosted === true || quotation.isPosted === 1}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={quotation.isPosted === true || quotation.isPosted === 1 ? t('quotations.alreadyConverted') : t('quotations.editQuotation')}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {!selectedCustomerId && (
                <div className="border-t px-4 sm:px-6 py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-muted-foreground text-center sm:text-left">
                      {t('quotations.showing')} {(pageNumber - 1) * pageSize + 1} {t('quotations.to')}{' '}
                      {Math.min(pageNumber * pageSize, totalCount)} {t('quotations.of')} {totalCount} {t('quotations.entries')}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground whitespace-nowrap">{t('quotations.pageSize')}:</span>
                        <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                          <SelectTrigger className="w-[100px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={pageNumber === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('quotations.previous')}</span>
                      </Button>
                      <div className="text-sm whitespace-nowrap">
                        {t('quotations.page')} {pageNumber} {t('quotations.of')} {totalPages || 1}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={pageNumber >= totalPages}
                      >
                        <span className="hidden sm:inline">{t('quotations.next')}</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
